import { useState, useMemo } from 'react';
import { YOUTOOB_VIDEOS, YOUTOOB_COMMENTS, BANNER_ADS } from '../parody-data.js';

const DEFAULT_AVATAR = '/ui/default-avatar.svg';

/* ------------------------------------------------------------------ */
/* Shared sub-components                                                */
/* ------------------------------------------------------------------ */

const VideoThumb = ({ video, width = 120, height = 90 }) => {
  const [failed, setFailed] = useState(false);
  const src = video.thumbnailPath ? `/youtoob/thumbnails/${video.thumbnailPath}` : null;
  return (
    <div className="yt-thumb-wrap" style={{ width, height }}>
      {src && !failed ? (
        <img
          src={src}
          alt={video.title}
          width={width}
          height={height}
          onError={() => setFailed(true)}
        />
      ) : (
        <div className="yt-thumb-placeholder">
          <span>{video.uploadDate ? video.uploadDate.slice(0, 3).toUpperCase() : 'VID'}</span>
        </div>
      )}
      {video.duration && <span className="yt-duration-badge">{video.duration}</span>}
    </div>
  );
};

const VideoTile = ({ video, onClick }) => (
  <div className="yt-video-tile" onClick={() => onClick(video)} data-testid={`video-item-${video.id}`}>
    <VideoThumb video={video} width={120} height={90} />
    <div className="yt-tile-info">
      <div className="yt-tile-title">{video.title}</div>
      <div className="yt-tile-meta">
        <img src={DEFAULT_AVATAR} className="yt-avatar-sm" alt="" />
        <span className="yt-tile-author">{video.author}</span>
      </div>
      <div className="yt-tile-views">{video.views} views</div>
      {video.uploadDate && <div className="yt-tile-date">{video.uploadDate}</div>}
    </div>
  </div>
);

const RelatedItem = ({ video, isActive, onClick }) => (
  <div
    className={`yt-related-item${isActive ? ' active' : ''}`}
    onClick={() => onClick(video)}
    data-testid={`video-item-${video.id}`}
  >
    <VideoThumb video={video} width={80} height={60} />
    <div className="yt-related-info">
      <div className="yt-related-title">{video.title}</div>
      <div className="yt-related-author">{video.author}</div>
      <div className="yt-related-views">{video.views} views</div>
    </div>
  </div>
);

const StarRating = ({ n = 4 }) => (
  <span className="yt-stars">
    {'★'.repeat(n)}{'☆'.repeat(5 - n)}
  </span>
);

/* ------------------------------------------------------------------ */
/* Main page component                                                  */
/* ------------------------------------------------------------------ */

export const YouToobPage = ({ onNavigate, initialVideoId = null, autoPlayInitial = false }) => {
  const getInitialVideo = () =>
    YOUTOOB_VIDEOS.find(v => v.youtubeId === initialVideoId) || YOUTOOB_VIDEOS[0];

  const [view,         setView]         = useState(autoPlayInitial && initialVideoId ? 'watch' : 'browse');
  const [selectedVideo,setSelectedVideo]= useState(getInitialVideo);
  const [isPlaying,    setIsPlaying]    = useState(Boolean(autoPlayInitial && getInitialVideo()?.youtubeId));
  const [searchQuery,  setSearchQuery]  = useState('');
  const [showPopup,    setShowPopup]    = useState(false);

  const randomAd = useMemo(
    () => BANNER_ADS[Math.floor(Math.random() * BANNER_ADS.length)],
    []
  );

  const mostViewed = useMemo(
    () => [...YOUTOOB_VIDEOS].sort((a, b) =>
      parseInt(b.views.replace(/,/g, '')) - parseInt(a.views.replace(/,/g, ''))
    ),
    []
  );

  const filteredVideos = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return YOUTOOB_VIDEOS;
    return YOUTOOB_VIDEOS.filter(v =>
      v.title?.toLowerCase().includes(q) || v.author?.toLowerCase().includes(q)
    );
  }, [searchQuery]);

  const relatedVideos = useMemo(
    () => YOUTOOB_VIDEOS.filter(v => v.id !== selectedVideo?.id),
    [selectedVideo]
  );

  const triggerAdPopup = () => {
    if (Math.random() > 0.7) {
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
    }
  };

  const handleVideoClick = (video) => {
    setSelectedVideo(video);
    setIsPlaying(false);
    setView('watch');
    triggerAdPopup();
  };

  const handlePlayClick = () => {
    if (!selectedVideo?.youtubeId) return;
    setIsPlaying(true);
    triggerAdPopup();
  };

  const goToBrowse = () => {
    setIsPlaying(false);
    setView('browse');
  };

  const embedUrl = selectedVideo?.youtubeId
    ? `https://www.youtube-nocookie.com/embed/${selectedVideo.youtubeId}?autoplay=1&rel=0`
    : null;

  /* ---------------------------------------------------------------- */
  /* Header (shared)                                                   */
  /* ---------------------------------------------------------------- */
  const header = (
    <div className="youtoob-header">
      <div className="youtoob-logo" onClick={goToBrowse}>
        <span className="logo-you">You</span>
        <span className="logo-toob">Boob</span>
        <span className="logo-beta">BETA</span>
      </div>
      <div className="youtoob-search">
        <input
          type="text"
          placeholder="Search videos..."
          value={searchQuery}
          onChange={e => { setSearchQuery(e.target.value); setView('browse'); }}
          className="search-input"
        />
        <button className="search-btn">Search</button>
      </div>
      <div className="youtoob-nav">
        <a href="#" onClick={e => { e.preventDefault(); goToBrowse(); }}>Home</a>
        <a href="#">Videos</a>
        <a href="#">Channels</a>
        <a href="#">Community</a>
        <a href="#">Upload (We Own It Now)</a>
      </div>
    </div>
  );

  /* ---------------------------------------------------------------- */
  /* Browse view                                                       */
  /* ---------------------------------------------------------------- */
  const browseView = (
    <div className="youtoob-content">
      <div className="youtoob-main">
        {/* Search results mode */}
        {searchQuery.trim() ? (
          <div className="yt-section">
            <div className="yt-section-heading">
              Search results for &ldquo;{searchQuery.trim()}&rdquo;
              &nbsp;({filteredVideos.length} videos)
            </div>
            <div className="yt-video-grid">
              {filteredVideos.length > 0
                ? filteredVideos.map(v => <VideoTile key={v.id} video={v} onClick={handleVideoClick} />)
                : <p className="yt-no-results">No videos found. Try a different search.</p>
              }
            </div>
          </div>
        ) : (
          <>
            {/* Featured Videos */}
            <div className="yt-section">
              <div className="yt-section-heading">Featured Videos</div>
              <div className="yt-featured-strip">
                {mostViewed.slice(0, 2).map(v => (
                  <div key={v.id} className="yt-featured-tile" onClick={() => handleVideoClick(v)}>
                    <VideoThumb video={v} width={160} height={120} />
                    <div className="yt-tile-info">
                      <div className="yt-tile-title">{v.title}</div>
                      <div className="yt-tile-meta">
                        <img src={DEFAULT_AVATAR} className="yt-avatar-sm" alt="" />
                        <span className="yt-tile-author">{v.author}</span>
                      </div>
                      <div className="yt-tile-views">{v.views} views</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Most Viewed Today */}
            <div className="yt-section">
              <div className="yt-section-heading">Most Viewed Today</div>
              <div className="yt-video-grid">
                {mostViewed.map(v => (
                  <VideoTile key={v.id} video={v} onClick={handleVideoClick} />
                ))}
              </div>
            </div>

            {/* Recently Added */}
            <div className="yt-section">
              <div className="yt-section-heading">Recently Added</div>
              <div className="yt-video-grid">
                {YOUTOOB_VIDEOS.slice().reverse().slice(0, 4).map(v => (
                  <VideoTile key={v.id} video={v} onClick={handleVideoClick} />
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="youtoob-sidebar">
        <div className="sidebar-section">
          <h3>Categories</h3>
          <ul className="category-list">
            <li>Comedy</li>
            <li>Music</li>
            <li>Entertainment</li>
            <li>Pets &amp; Animals</li>
            <li>How To &amp; Style</li>
            <li>Gaming</li>
          </ul>
        </div>
        <div className="sidebar-ad-box">
          <div className="ad-content blinking">
            Congratulations!<br />
            You are today's prize viewer.<br />
            <button>Claim Prize</button>
          </div>
        </div>
        <div className="sidebar-section">
          <h3>Top Rated</h3>
          <div className="video-list">
            {mostViewed.slice(0, 4).map(v => (
              <RelatedItem
                key={v.id}
                video={v}
                isActive={false}
                onClick={handleVideoClick}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  /* ---------------------------------------------------------------- */
  /* Watch view                                                        */
  /* ---------------------------------------------------------------- */
  const watchView = (
    <div className="youtoob-content">
      <div className="youtoob-main">
        {/* Back link */}
        <div className="yt-back-bar">
          <button className="yt-back-link" onClick={goToBrowse}>
            &laquo; Back to Videos
          </button>
        </div>

        {/* Player */}
        <div className="video-player">
          {isPlaying && embedUrl ? (
            <div className="video-screen playing">
              <iframe
                title={selectedVideo?.title || 'YouBoob Player'}
                src={embedUrl}
                className="native-video-embed"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>
          ) : (
            <div
              className="video-screen"
              onClick={handlePlayClick}
              style={{ cursor: selectedVideo?.youtubeId ? 'pointer' : 'not-allowed' }}
            >
              <VideoThumb video={selectedVideo} width={320} height={240} />
              {selectedVideo?.youtubeId ? (
                <>
                  <div className="play-button">&#9654;</div>
                  <div className="video-overlay">
                    <span>Click to play</span>
                    <span className="buffering">Buffering... please wait.</span>
                  </div>
                </>
              ) : (
                <div className="video-overlay removed">
                  <span>Video Removed</span>
                  <span className="buffering">This video is no longer available.</span>
                </div>
              )}
            </div>
          )}

          <div className="video-controls">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: isPlaying ? '18%' : '0%' }} />
            </div>
            <div className="control-buttons">
              <button onClick={() => setIsPlaying(false)}>&lt;&lt;</button>
              <button onClick={handlePlayClick}>&gt;</button>
              <button>&gt;&gt;</button>
              <span className="time">{isPlaying ? '0:34 / 3:45' : '0:00 / 3:45'}</span>
              <button>VOL</button>
              <button>SIZE</button>
            </div>
          </div>
        </div>

        {/* Video info */}
        <div className="video-info">
          <h1 className="video-title">{selectedVideo?.title}</h1>
          <div className="video-meta">
            <span className="views">{selectedVideo?.views} views</span>
            <span className="date">Added: {selectedVideo?.uploadDate || 'March 15, 2007'}</span>
          </div>
          <div className="video-author">
            <img src={DEFAULT_AVATAR} className="yt-avatar" alt="user avatar" />
            <a href="#" className="author-name">{selectedVideo?.author}</a>
            <button className="subscribe-btn">Subscribe</button>
          </div>
          <div className="video-actions">
            <button>Favorite</button>
            <button>Add to Playlist</button>
            <button>Flag</button>
            <button>Share</button>
          </div>
          <div className="video-rating">
            <span>Rate this video:</span>
            <StarRating n={4} />
            <span className="rating-count">(1,234 ratings)</span>
          </div>
        </div>

        {/* Comments */}
        <div className="comments-section">
          <h3>Comments ({YOUTOOB_COMMENTS.length})</h3>
          <div className="comment-form">
            <textarea placeholder="Leave a comment..." rows="2" />
            <button className="post-btn">Post Comment</button>
          </div>
          <div className="comments-list">
            {YOUTOOB_COMMENTS.map((comment, i) => (
              <div key={i} className="comment">
                <img src={DEFAULT_AVATAR} className="yt-avatar comment-avatar" alt="user" />
                <div className="comment-content">
                  <span className="comment-user">{comment.user}</span>
                  <span className="comment-text">{comment.text}</span>
                  <div className="comment-actions">
                    <span>Rate ({comment.likes})</span>
                    <a href="#">Reply</a>
                    <a href="#">Flag</a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Related videos sidebar */}
      <div className="youtoob-sidebar">
        <div className="sidebar-section">
          <h3>Related Videos</h3>
          <div className="video-list">
            {relatedVideos.map(v => (
              <RelatedItem
                key={v.id}
                video={v}
                isActive={false}
                onClick={handleVideoClick}
              />
            ))}
          </div>
        </div>
        <div className="sidebar-ad-box">
          <div className="ad-content blinking">
            Congratulations!<br />
            You are today's prize viewer.<br />
            <button>Claim Prize</button>
          </div>
        </div>
      </div>
    </div>
  );

  /* ---------------------------------------------------------------- */
  /* Render                                                            */
  /* ---------------------------------------------------------------- */
  return (
    <div className="youtoob-page" data-testid="youtoob-page">
      {showPopup && (
        <div className="popup-ad" data-testid="popup-ad">
          <div className="popup-content">
            <button className="popup-close" onClick={() => setShowPopup(false)}>X</button>
            <div className="popup-text" style={{ color: randomAd.color }}>{randomAd.text}</div>
            <div className="popup-subtext">{randomAd.subtext}</div>
            <button className="popup-btn">Continue</button>
          </div>
        </div>
      )}

      {header}

      <div className="youtoob-banner-ad">
        <marquee scrollamount="3" style={{ backgroundColor: '#ffffcc' }}>
          {randomAd.text} — {randomAd.subtext} — CLICK NOW!!!
        </marquee>
      </div>

      {view === 'browse' ? browseView : watchView}

      <div className="youtoob-footer">
        <div className="footer-links">
          <a href="#">About</a> | <a href="#">Help</a> | <a href="#">Terms</a> |{' '}
          <a href="#">Privacy</a> | <a href="#">Copyright</a>
        </div>
        <p>© 2007 YouBoob LLC. Broadcast yourself. Service provided by Profit Dynamics International™.</p>
        <button className="back-btn" onClick={() => onNavigate('portal')}>Back to Portal</button>
      </div>
    </div>
  );
};

export default YouToobPage;
